import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router"

import { GET, httpOk } from "helpers/api"
import { formatDate } from "helpers/helpers"

import { cardStyle, mutedStyle, Skeleton, TabError } from "./parts/common"
import Overview from "./tabs/Overview"
import Brands from "./tabs/Brands"
import Locations from "./tabs/Locations"
import Gtv from "./tabs/Gtv"
import Revenue from "./tabs/Revenue"
import Retention from "./tabs/Retention"
import Product from "./tabs/Product"
import Agents from "./tabs/Agents"
import Flow from "./tabs/Flow"
import Lost from "./tabs/Lost"

/**
 * Аналитический дашборд компании.
 *
 * Одна вкладка — один эндпоинт, и запрос уходит только когда вкладку открыли.
 * Общего «толстого» ответа нет намеренно: именно из-за него дашборды и грузятся
 * по десять секунд. Данные читаются из ночной свёртки, поэтому ответ мгновенный.
 */

const TABS = [
	{ id: "overview", label: "Обзор", url: "/admin/analytics/overview", component: Overview },
	{ id: "brands", label: "Бренды", url: "/admin/analytics/brands", component: Brands },
	{ id: "locations", label: "Точки", url: "/admin/analytics/locations", component: Locations },
	{ id: "gtv", label: "Оборот", url: "/admin/analytics/gtv", component: Gtv },
	{ id: "revenue", label: "Выручка", url: "/admin/analytics/revenue", component: Revenue },
	{ id: "retention", label: "Удержание", url: "/admin/analytics/retention", component: Retention },
	// Свой период (3 / 6 / 12 мес.) и свой фильтр по партнёру — данные грузит сама вкладка
	{ id: "flow", label: "Пришли и ушли", component: Flow, selfLoading: true },
	// Свой месяц вместо общего периода страницы — данные грузит сама вкладка
	{ id: "lost", label: "Потери месяца", component: Lost, selfLoading: true },
	{ id: "product", label: "Продукт", url: "/admin/analytics/product", component: Product },
	{ id: "agents", label: "Агенты", url: "/admin/analytics/agents", component: Agents },
]

// По умолчанию 12 месяцев, а не 30 дней: дашборд отвечает на вопрос «куда мы идём»,
// и на месячном окне этого не видно.
const PRESETS = [
	{ id: "12m", label: "12 месяцев" },
	{ id: "24m", label: "24 месяца" },
	{ id: "year", label: "Этот год" },
	{ id: "quarter", label: "Квартал" },
	{ id: "month", label: "Месяц" },
]

function Index() {
	const [searchParams, setSearchParams] = useSearchParams()

	const [tab, setTab] = useState(() => {
		const fromUrl = searchParams.get("tab")
		return TABS.some((item) => item.id === fromUrl) ? fromUrl : "overview"
	})
	const [preset, setPreset] = useState(() => {
		const fromUrl = searchParams.get("preset")
		return PRESETS.some((item) => item.id === fromUrl) ? fromUrl : "12m"
	})
	const [currency, setCurrency] = useState(() => (searchParams.get("currency") === "usd" ? "usd" : "uzs"))

	// Кэш вкладок: { overview: {...}, brands: {...} }. Открытая один раз вкладка
	// повторного запроса не делает.
	const [cache, setCache] = useState({})
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	// Защита от гонки: пользователь щёлкает по вкладкам быстрее, чем отвечает сервер,
	// и ответ медленной вкладки прилетает уже после ухода на другую. Помечаем каждый
	// запрос номером и принимаем только последний. (AbortController здесь не годится:
	// общий helpers/api его не пробрасывает.)
	const requestId = useRef(0)

	const loadTab = useCallback(async (tabId, force = false) => {
		const config = TABS.find((item) => item.id === tabId)
		if (!config) return
		if (config.selfLoading) {
			requestId.current++
			setError(null)
			setLoading(false)
			return
		}

		setError(null)
		if (!force && cache[tabId]) {
			// Вкладка уже загружена. Номер всё равно двигаем: если в этот момент
			// летит ответ другой вкладки, он не должен оставить нас в «загрузке».
			requestId.current++
			setLoading(false)
			return
		}

		const id = ++requestId.current
		setLoading(true)

		const response = await GET(config.url, { preset }, { loader: false })

		if (id !== requestId.current) return

		setLoading(false)
		if (httpOk(response)) {
			setCache((prev) => ({ ...prev, [tabId]: response.data }))
		} else {
			setError(response?.data?.message || "Не удалось загрузить данные")
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preset, cache])

	useEffect(() => {
		loadTab(tab)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab, preset])

	// Смена периода обнуляет кэш ВСЕХ вкладок, но перезагружает только активную —
	// иначе один клик по «24 месяца» запускает восемь тяжёлых запросов разом.
	function changePreset(value) {
		setCache({})
		setPreset(value)
		updateUrl({ preset: value })
	}

	function changeTab(value) {
		setTab(value)
		updateUrl({ tab: value })
	}

	function changeCurrency(value) {
		// Пересчёт делает фронт по курсам из ответа, поэтому перезапрос не нужен.
		setCurrency(value)
		updateUrl({ currency: value })
	}

	function updateUrl(patch) {
		const next = new URLSearchParams(searchParams)
		Object.entries(patch).forEach(([key, value]) => next.set(key, value))
		if (!next.get("tab")) next.set("tab", tab)
		setSearchParams(next, { replace: true })
	}

	const data = cache[tab]
	const activeConfig = TABS.find((item) => item.id === tab)
	const ActiveTab = activeConfig?.component
	const selfLoading = Boolean(activeConfig?.selfLoading)
	const hasRates = data?.usd_rates && Object.keys(data.usd_rates).length > 0

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
				<h4 className="vertical-center mb-0">Аналитика проекта</h4>

				{/* У вкладки со своим периодом общий переключатель ничего бы не менял — прячем */}
				<div className={`align-items-center flex-wrap gap-2 ${selfLoading ? "d-none" : "d-flex"}`}>
					<div className="btn-group btn-group-sm">
						{PRESETS.map((item) => (
							<button
								key={item.id}
								type="button"
								className={`btn ${preset === item.id ? "btn-primary" : "btn-outline-primary"}`}
								onClick={() => changePreset(item.id)}
							>
								{item.label}
							</button>
						))}
					</div>

					<div className="btn-group btn-group-sm">
						<button
							type="button"
							className={`btn ${currency === "uzs" ? "btn-primary" : "btn-outline-primary"}`}
							onClick={() => changeCurrency("uzs")}
						>
							UZS
						</button>
						<button
							type="button"
							className={`btn ${currency === "usd" ? "btn-primary" : "btn-outline-primary"}`}
							onClick={() => changeCurrency("usd")}
							disabled={!hasRates}
							title={hasRates ? "" : "Курсы ЦБ ещё не загружены"}
						>
							USD
						</button>
					</div>
				</div>
			</div>

			<div className="card mb-3" style={cardStyle}>
				<div className="card-body py-2 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
					<ul className="nav nav-pills gap-1" style={{ flexWrap: "wrap" }}>
						{TABS.map((item) => (
							<li className="nav-item" key={item.id}>
								<button
									type="button"
									className={`nav-link py-1 px-3 ${tab === item.id ? "active" : ""}`}
									style={{ fontSize: "13px" }}
									onClick={() => changeTab(item.id)}
								>
									{item.label}
								</button>
							</li>
						))}
					</ul>

					<div style={mutedStyle} className={selfLoading ? "d-none" : ""}>
						{data?.calculated_at
							? `свёртка обновлена: ${formatDate(data.calculated_at, "dd.MM.yyyy HH:mm")}`
							: "свёртка ещё не считалась"}
					</div>
				</div>
			</div>

			{!selfLoading && error && <TabError message={error} onRetry={() => loadTab(tab, true)} />}

			{selfLoading && ActiveTab && <ActiveTab />}

			{!selfLoading && !error && (loading || !data) && <Skeleton rows={3} />}

			{!selfLoading && !error && !loading && data && ActiveTab && (
				<ActiveTab
					data={data}
					currency={currency}
					rates={data.usd_rates || {}}
					preset={preset}
					onReload={() => loadTab(tab, true)}
				/>
			)}
		</>
	)
}

export default Index
